import { PlannedNotificationDefaults } from "./remaining-settings-sections";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  notificationChannels,
  type NotificationEvent,
  type NotificationChannel,
  type NotificationPreferenceList,
} from "@zworks/console-contracts";
import { useState } from "react";
import { LoadingState, NotConnectedState } from "@/components/states";
import { Button } from "@/components/ui/button";
import { notificationsQuery, updateNotifications, ApiError } from "@/lib/api";
import { SettingsFailure } from "./settings-feedback";
import { settingsAccessLost } from "@/lib/api";
import { settingsSelectClass } from "./general-settings-section";
const labels: Record<NotificationEvent, string> = {
  "run-finished": "Run finished",
  "run-waiting-approval": "Approval requested",
  "approval-decided": "Approval decided",
  "credits-low": "Balance is low",
  "grant-expiring": "Starter credits expiring",
  "member-invited": "Member invited",
};
const channels: Record<NotificationChannel, string> = {
  off: "Off",
  "in-app": "In app",
  "in-app-and-email": "In app and email",
};
export function NotificationsSection({ projectId }: { projectId: string }) {
  const queryClient = useQueryClient();
  const query = useQuery(notificationsQuery(projectId));
  const [draft, setDraft] = useState<NotificationPreferenceList | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: (preferences: NotificationPreferenceList) =>
      updateNotifications(projectId, preferences),
    onSuccess: (value) => {
      queryClient.setQueryData(notificationsQuery(projectId).queryKey, value);
      setDraft(null);
      setNotice("Notification preferences saved.");
    },
    onError: async (error) => {
      if (settingsAccessLost(error)) {
        setDraft(null);
        queryClient.removeQueries({ queryKey: notificationsQuery(projectId).queryKey });
      } else if (error instanceof ApiError && error.status === 409) {
        setDraft(null);
        await query.refetch();
      }
    },
  });
  if (settingsAccessLost(save.error)) return <SettingsFailure error={save.error} />;
  if (query.isPending) return <LoadingState label="Loading notification preferences" rows={4} />;
  if (query.isError)
    return <SettingsFailure error={query.error} retry={() => void query.refetch()} />;
  const preferences = draft ?? query.data;
  return (
    <section className="flex max-w-3xl flex-col gap-4" aria-label="Notification preferences">
      <h2 className="text-lg font-semibold">Notifications</h2>
      <p className="text-sm text-muted-foreground">
        Choose how you receive alerts for this project. These preferences apply only to you.
      </p>
      <PlannedNotificationDefaults />
      <NotConnectedState
        title="Notifications are not connected yet"
        description="Preferences are saved, but nothing sends email or in-app alerts yet. Approval waiting and run alerts need a mail sender first."
      />
      {notice && (
        <p role="status" className="text-sm text-muted-foreground">
          {notice}
        </p>
      )}
      {save.error && <SettingsFailure error={save.error} />}
      <form
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (draft && !save.isPending) save.mutate(preferences);
        }}
      >
        <fieldset
          disabled={save.isPending}
          className="flex flex-col gap-4 rounded-lg border bg-card p-4"
        >
          <legend className="px-1 text-sm font-medium">For you</legend>
          <label className="flex flex-wrap items-center justify-between gap-2 text-sm">
            Channel for all events
            <select
              className={settingsSelectClass}
              value={
                preferences.every((row) => row.channel === preferences[0]?.channel)
                  ? (preferences[0]?.channel ?? "")
                  : ""
              }
              onChange={(event) => {
                setNotice(null);
                setDraft(
                  preferences.map((row) => ({
                    ...row,
                    channel: event.target.value as NotificationChannel,
                  })),
                );
              }}
            >
              <option value="" disabled>
                Custom per event
              </option>
              {notificationChannels.map((channel) => (
                <option key={channel} value={channel}>
                  {channels[channel]}
                </option>
              ))}
            </select>
          </label>
          {preferences.map((preference) => (
            <label
              key={preference.event}
              className="flex flex-wrap items-center justify-between gap-2 text-sm"
            >
              {labels[preference.event]}
              <select
                className={settingsSelectClass}
                value={preference.channel}
                onChange={(event) => {
                  setNotice(null);
                  setDraft(
                    preferences.map((row) =>
                      row.event === preference.event
                        ? { ...row, channel: event.target.value as NotificationChannel }
                        : row,
                    ),
                  );
                }}
              >
                {notificationChannels.map((channel) => (
                  <option key={channel} value={channel}>
                    {channels[channel]}
                  </option>
                ))}
              </select>
            </label>
          ))}
        </fieldset>
        <div className="flex justify-end">
          <Button
            type="submit"
            disabled={
              save.isPending || !draft || JSON.stringify(draft) === JSON.stringify(query.data)
            }
          >
            {save.isPending ? "Saving…" : "Save notifications"}
          </Button>
        </div>
      </form>
    </section>
  );
}
